import { useMemo } from "react";
import { useSelector } from "react-redux";
import m from "moment";
import { STATUS } from "@p81-common/shell-core-js";
import { isCheckpointDomain } from "@p81-common/shell-core-js";

const TRIAL_DEFAULT_THRESHOLD_DAYS = 15;
const TRIAL_WARNING_THRESHOLD_DAYS = 7;

export const TRIAL_BANNER_TARGET_PAGES = [
  "/get-started",
  "/explore",
  "/dashboard",
  "/team/members",
  "/network-access/networks",
  "/security/web-filter-rules",
];

export type TrialCountdownState = "default" | "warning" | "error" | null;

export interface TrialCountdownData {
  show: boolean;
  daysLeft: number;
  state: TrialCountdownState;
  cancelDate: string;
}

export const useTrialCountdown = (): TrialCountdownData => {
  const { subscriptionData = {}, isPureEval } = useSelector((s: any) => ({
    subscriptionData: s.subscriptionGet?.data?.subscription,
    isPureEval: s.cpAppEvalSKUsPost?.data?.isPureEval,
  }));

  return useMemo(() => {
    const { cancelledAt, status: subscriptionStatus } = subscriptionData;

    // Main condition: isCheckpointDomain must be true to show the banner
    if (
      !isCheckpointDomain ||
      !isPureEval ||
      !cancelledAt ||
      subscriptionStatus !== STATUS.CANCEL_PLANNED
    ) {
      return { show: false, daysLeft: 0, state: null, cancelDate: "" };
    }

    const daysLeft = m(cancelledAt).diff(m(), "days");

    let state: TrialCountdownState = null;
    if (daysLeft > TRIAL_DEFAULT_THRESHOLD_DAYS) {
      state = "default";
    } else if (daysLeft > TRIAL_WARNING_THRESHOLD_DAYS) {
      state = "warning";
    } else {
      // Includes 0 days and negative values (trial expired)
      state = "error";
    }

    return {
      show: true,
      daysLeft,
      state,
      cancelDate: m(cancelledAt).format("MMM D, YYYY"),
    };
  }, [subscriptionData, isPureEval]);
};
